import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import GradientButton from './GradientButton';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const isCurrentPage = (path) => {
    return location.pathname === path;
  };

  const NavItem = ({ to, textKey, mobile = false }) => (
    <Link
      to={to}
      className={`
        ${mobile
          ? 'block w-full text-left px-4 py-3 rounded-lg text-base font-medium'
          : 'text-base font-medium'
        }
        text-white hover:text-secondary transition-all duration-300
        ${isCurrentPage(to)
          ? 'text-secondary font-semibold' + (mobile ? ' bg-secondary/10' : '')
          : mobile ? 'hover:bg-white/5' : ''
        }
      `}
      onClick={() => setIsMenuOpen(false)}
    >
      {t(`common.${textKey}`)}
    </Link>
  );

  const navItems = [
    { to: '/professionals', textKey: 'nutritionists' },
    { to: '/tdee-calculator', textKey: 'tdeeCalculator' },
    { to: '/blog', textKey: 'blog' },
  ];

  return (
    <header className={`bg-background text-white transition-all duration-300 ${isSticky ? 'fixed top-0 left-0 right-0 z-50 shadow-lg backdrop-blur-sm bg-background/95' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-18">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center group focus:outline-none rounded-lg transition-all duration-300"
            aria-label="Home"
          >
            <div className="relative">
              {/* Gradient glow effect on hover/focus */}
              <div className="absolute inset-0 bg-gradient-to-r from-lime via-yellow to-cyan opacity-0 group-hover:opacity-20 group-focus:opacity-30 blur-xl transition-all duration-500 rounded-lg scale-110"></div>

              {/* Logo image with smooth scale and filter */}
              <img
                src="/images/fitnessbro_logo.webp"
                alt="Fitnessbro Logo"
                width="256"
                height="256"
                className="h-9 md:h-10 w-auto relative z-10 transition-all duration-300 ease-out group-hover:scale-105 group-active:scale-95 group-hover:brightness-110 group-hover:drop-shadow-[0_0_8px_rgba(156,255,30,0.5)]"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
            {navItems.map((item) => (
              <NavItem key={item.to} to={item.to} textKey={item.textKey} />
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSelector />
            <div className="h-6 w-px bg-white/20"></div>
            <GradientButton
              variant="primary"
              href="https://forms.gle/b73YhN5WxGSTZn618"
              className="!px-5 !py-2 !text-sm !min-w-[110px]"
              trackingEvent="click_dicci_la_tua"
              trackingLabel="header_desktop"
            >
              {t('common.shareOpinion')}
            </GradientButton>
            <GradientButton
              variant="secondary"
              href="https://drive.google.com/file/d/1QJEeyfEdiYeDN15PYUfwrHjOSWd_MdAA/view?usp=sharing"
              className="!px-5 !py-2 !text-sm !min-w-[110px]"
              trackingEvent="click_scopri_di_piu"
              trackingLabel="header_desktop"
            >
              {t('common.brochure')}
            </GradientButton>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="md:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-secondary"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden overflow-hidden animate-fade-in">
          <div className="px-4 pt-4 pb-6 space-y-4 bg-gradient-to-b from-background to-background/95 backdrop-blur-lg border-t border-white/10">
            {/* Navigation Links */}
            <nav className="space-y-1">
              {navItems.map((item) => (
                <NavItem key={item.to} to={item.to} textKey={item.textKey} mobile={true} />
              ))}
            </nav>

            {/* Divider */}
            <div className="h-px bg-white/10 my-4"></div>

            {/* Language Selector */}
            <div className="flex justify-center py-2">
              <LanguageSelector />
            </div>

            {/* Divider */}
            <div className="h-px bg-white/10 my-4"></div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 px-2">
              <GradientButton
                variant="primary"
                href="https://forms.gle/b73YhN5WxGSTZn618"
                className="w-full !text-base !py-3"
                trackingEvent="click_dicci_la_tua"
                trackingLabel="header_mobile"
              >
                {t('common.shareOpinion')}
              </GradientButton>
              <GradientButton
                variant="secondary"
                href="https://drive.google.com/file/d/1QJEeyfEdiYeDN15PYUfwrHjOSWd_MdAA/view?usp=sharing"
                className="w-full !text-base !py-3"
                trackingEvent="click_scopri_di_piu"
                trackingLabel="header_mobile"
              >
                {t('common.brochure')}
              </GradientButton>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
